import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { useChatStore } from '@modules/chat/store/chatStore';
import { PureList } from '@common/components/list';
import { combineClassNames } from '@common/utils/combineClassNames';
import Placeholder from '@common/components/placeholder';

import Topbar from './Topbar';
import ArchivedChats from './ArchivedChats';
import ConversationItem from '../conversation-item';
import Spinner from '@common/components/spinner';
import { EConversationFilterTypes, ESearhFilterTypes, FullConversation } from '@modules/chat/types/objects';
import { EConversationFilters, RouteParams } from '@modules/chat/definitions';

interface ChatsProps {
  basePath: string;
}

const Chats = (props: ChatsProps) => {
  const {
    conversations,
    currentConversation,
    selectedFilter,
    loading,
    meta,
    refetch,
    conversationId,
    usersStatus,
    search,
    handleOpenProfile,
    handleToggleNotifications,
    handleToggleArchive,
    handleRemoveConversation,
    handleEditGroupAdmin,
    handleEditParticipants,
    handleLeave,
    handleUpdateConversation,
    handleConversationPinOrUnpin,
    handleConversationMarkAsReadOrUnread,
    handleConversationFavouriteOrUnfavourite,
    handleConversationMuteOrUnmute,
    handleOpenConversation,
    searchFilter,
    selectedSearchIndex,
    setSelectedSearchIndex,
    isSearchFocused,
  } = useChatStore();
  const { basePath } = props;
  const { filter } = useParams<RouteParams>();
  const { t } = useTranslation();

  const pagination = useMemo(() => meta?.pagination, [meta?.pagination]);

  const rowProps = useMemo(() => ({
    basePath,
    currentConversationId: conversationId,
    usersStatus,
    onOpenProfile: handleOpenProfile,
    onToggleNotifications: handleToggleNotifications,
    onToggleArchive: handleToggleArchive,
    onRemoveConversation: handleRemoveConversation,
    onEditGroupAdmin: handleEditGroupAdmin,
    onEditParticipants: handleEditParticipants,
    onLeave: handleLeave,
    onUpdateConversation: handleUpdateConversation,
    onConversationPinOrUnpin: handleConversationPinOrUnpin,
    onConversationMarkAsReadOrUnread: handleConversationMarkAsReadOrUnread,
    onConversationFavouriteOrUnfavourite: handleConversationFavouriteOrUnfavourite,
    onConversationMuteOrUnmute: handleConversationMuteOrUnmute,
    onOpenConversation: handleOpenConversation,
  }), [
    basePath,
    conversationId,
    usersStatus,
    handleOpenProfile,
    handleToggleNotifications,
    handleToggleArchive,
    handleRemoveConversation,
    handleEditGroupAdmin,
    handleEditParticipants,
    handleLeave,
    handleUpdateConversation,
    handleConversationPinOrUnpin,
    handleConversationMarkAsReadOrUnread,
    handleConversationFavouriteOrUnfavourite,
    handleConversationMuteOrUnmute,
    handleOpenConversation,
  ]);

  const isArchived = useMemo(() => {
    return filter === EConversationFilters.ARCHIVED;
  }, [filter]);

  const filteredConversations = useMemo(() => {
    if (selectedFilter === EConversationFilterTypes.UNREAD) {
      return conversations.filter((conversation) => {
        return conversation.id === currentConversation?.id || conversation.last_message?.seen === false;
      });
    }
    return conversations;
  }, [conversations, currentConversation, selectedFilter]);

  const placeholder = useMemo(() => {
    switch (true) {
      case filter === EConversationFilters.ARCHIVED:
        return t('chat:archive_placeholder_title');
      case isSearchFocused && (!search || search === '') && searchFilter === ESearhFilterTypes.CHATS:
        return t('chat:search_placeholder_chats');
      case isSearchFocused && (!search || search === '') && searchFilter === ESearhFilterTypes.MESSAGES:
        return t('chat:search_placeholder_messages');
      default:
        return t('common:no_results');
    }
  }, [filter, t, search, isSearchFocused, searchFilter]);

  const description = useMemo(() => {
    switch (true) {
      case isSearchFocused && (!search || search === '') && searchFilter === ESearhFilterTypes.CHATS:
        return t('chat:search_placeholder_description_chats');
      case isSearchFocused && (!search || search === '') && searchFilter === ESearhFilterTypes.MESSAGES:
        return t('chat:search_placeholder_description_messages');
      default:
        return '';
    }
  }, [filter, t, search, isSearchFocused, searchFilter]);

  const heightClass = useMemo(() => {
    if (!!search || isSearchFocused) {
      return 'tw-h-[calc(100vh-184px)]';
    }
    if (isArchived) {
      return 'tw-h-[calc(100vh-121px)]';
    }
    return 'tw-h-[calc(100vh-300px)]';
  }, [search, isSearchFocused, isArchived]);

  return (
    <div
      className={`
        chat-sidebar
        tw-flex-none tw-w-[400px]  tw-bg-white
        tw-border-r tw-border-[#F1F2F5] tw-sticky tw-top-[72px] tw-z-[1]
        tw-overflow-y-hidden
      `}
    >
      {!isArchived && (
        <Topbar basePath={basePath} />
      )}
      {!search && !isSearchFocused && (
        <ArchivedChats basePath={basePath} />
      )}
      <PureList
        containerClassName={combineClassNames('MyConversations__list', {
          'MyConversations__list--has-more': !!pagination?.next_cursor,
          [heightClass]: true,
        })}
        items={filteredConversations as FullConversation[]}
        renderRow={ConversationItem}
        rowProps={(item, index) => ({
          ...rowProps,
          index,
          selectedIndex: searchFilter === 'messages' ? selectedSearchIndex : undefined,
          onClick: () => {
            if (searchFilter === 'messages') setSelectedSearchIndex(index);
          },
        })}
        footer={
          <>
            {loading && <Spinner centered />}
            <div>
              {!!pagination?.next_cursor && (
                <div className="MyConversations__List__ShowMore" onClick={refetch}>
                  {t('chat:conversations_load_more')}
                </div>
              )}
            </div>
          </>
        }
        placeholder={
          loading
            ? <Spinner centered />
            : <Placeholder title={placeholder} description={description} />
        }
      />
    </div>
  );
};

export default React.memo(Chats);
